import { FC, useEffect, useState } from 'react';

import { Button, TextField } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import HtmlContent from '@telialt/common-html-content/src/HtmlContent';
import { TextButton } from '@telialt/common-text-button/src';
import { withParams } from '@telialt/utils/src/helpers';

import { ORDER_CART_ENTRY_ID } from '../constants';
import { CheckIcon, DiscountAppliedEntryListItem, DiscountForm, EntryListItem } from './entries.styles';

type DiscountEntryProps = {
    voucherCode: string;
    onSubmitCode: (code: string) => void;
    isLoading?: boolean;
    isInvalid?: boolean;
};

const FORM_NAME = 'discount-form-in-entry';

export const DiscountEntry: FC<DiscountEntryProps> = ({ voucherCode, onSubmitCode, isLoading, isInvalid }) => {
    const { t } = useTranslations();
    const [status, setStatus] = useState<'INIT' | 'OPEN' | 'SUBMITTED'>(!!voucherCode ? 'SUBMITTED' : 'INIT');
    const label = t['order_cart.entry_discount.discount_form_label'];

    useEffect(() => {
        if (voucherCode && status !== 'SUBMITTED') {
            setStatus('SUBMITTED');
        }
    }, [voucherCode, status]);

    if (status === 'INIT') {
        return (
            <EntryListItem data-test={ORDER_CART_ENTRY_ID.DISCOUNT} aria-label={label}>
                <TextButton onClick={() => setStatus('OPEN')}>
                    {t['order_cart.entry_discount.discount_form_toggle_button']}
                </TextButton>
            </EntryListItem>
        );
    }
    if (status === 'OPEN') {
        return (
            <EntryListItem data-test={ORDER_CART_ENTRY_ID.DISCOUNT} aria-label={label}>
                <DiscountForm
                    onSubmit={(event) => {
                        event.preventDefault();
                        const formData = new FormData(event.currentTarget);
                        const code = formData.get(FORM_NAME)?.toString();
                        if (code) {
                            onSubmitCode(code);
                        }
                    }}
                    autoComplete="off"
                >
                    <TextField
                        name={FORM_NAME}
                        type="text"
                        disabled={isLoading}
                        errorText={isInvalid ? t['order_cart.entry_discount.discount_form_invalid'] : undefined}
                        label={label}
                        helperText={t['order_cart.entry_discount.discount_form_disclaimer']}
                    />
                    <Button disabled={isLoading} loading={isLoading} type="submit" variant="secondary" size="md">
                        {isLoading
                            ? t['order_cart.entry_discount.discount_form_button.loading']
                            : t['order_cart.entry_discount.discount_form_button']}
                    </Button>
                </DiscountForm>
            </EntryListItem>
        );
    }
    if (status === 'SUBMITTED') {
        return (
            <DiscountAppliedEntryListItem data-test={ORDER_CART_ENTRY_ID.DISCOUNT} aria-label={label}>
                <CheckIcon />
                <HtmlContent
                    content={withParams(t['order_cart.entry_discount.discount_form_success'], { code: voucherCode })}
                />
            </DiscountAppliedEntryListItem>
        );
    }

    return null;
};
